/**
 * §14.2 — one InstancedMesh per projectile visual class. `visual_class` is a stable
 * integer that R2 stamps on every projectile at spawn; this table is the only place
 * the weapon → class mapping is defined, so R2 only has to be consistent per weapon.
 *
 * 12 player classes + 3 enemy classes, exactly as budgeted in the §14.2 draw-call table.
 */

export const VC_SPEAR = 0;
export const VC_KRILL_PELLET = 1;
export const VC_GAZE_BEAM = 2;
export const VC_HALO_ORB = 3;
export const VC_NOVA_RING = 4;
export const VC_MINE = 5;
export const VC_CHAIN_ARC = 6;
export const VC_SCION = 7;
export const VC_ZONE_POOL = 8;
export const VC_SHROUD = 9;
export const VC_MAW = 10;
export const VC_SEARCHLIGHT_BEAM = 11;

export const VC_VENT_SPIT = 12;
export const VC_INK_GLOB = 13;
export const VC_BRISTLE_DART = 14;

export const PLAYER_VISUAL_CLASS_COUNT = 12;
export const ENEMY_VISUAL_CLASS_COUNT = 3;
export const VISUAL_CLASS_COUNT = PLAYER_VISUAL_CLASS_COUNT + ENEMY_VISUAL_CLASS_COUNT;

/** `weapons.json` id → visual class. Evolutions reuse their base weapon's class. */
const BY_WEAPON: Readonly<Record<string, number>> = {
  'weapon.spear-jelly-lance': VC_SPEAR,
  'weapon.krill-scatter': VC_KRILL_PELLET,
  'weapon.barreleye-gaze': VC_GAZE_BEAM,
  'weapon.copepod-halo': VC_HALO_ORB,
  'weapon.atolla-nova': VC_NOVA_RING,
  'weapon.angler-lantern-mine': VC_MINE,
  'weapon.siphonophore-chain-whip': VC_CHAIN_ARC,
  'weapon.bobtail-scion': VC_SCION,
  'weapon.brine-pool': VC_ZONE_POOL,
  'weapon.vampire-shroud': VC_SHROUD,
  'weapon.gulper-maw': VC_MAW,
  'weapon.loosejaw-searchlight': VC_SEARCHLIGHT_BEAM,

  'enemy.ventspitter': VC_VENT_SPIT,
  'enemy.gloomsquid': VC_INK_GLOB,
  'enemy.nettle-anemone': VC_BRISTLE_DART,
};

/** §11.2.2 hue per class. Enemy projectiles borrow their attacker's archetype hue. */
const TOKEN_BY_CLASS: readonly string[] = [
  'pal.friendly.lance',
  'pal.friendly.krill',
  'pal.friendly.gaze',
  'pal.friendly.halo',
  'pal.friendly.nova',
  'pal.friendly.mine',
  'pal.friendly.chain',
  'pal.friendly.scion',
  'pal.friendly.pool',
  'pal.friendly.shroud',
  'pal.friendly.maw',
  'pal.friendly.sweep',
  'pal.enemy.ventspitter',
  'pal.enemy.gloomsquid',
  'pal.enemy.nettle-anemone',
];

/**
 * §11.3 emissive intensity per class: friendly projectiles 1.8, beams 2.0, enemy 1.2.
 *
 * AMD-7 R4 — two exceptions, both on the `lance` / `halo` pair.
 *
 * §11.2.2 pins `pal.friendly.lance` #FFC84D and `pal.friendly.halo` #FFB85C 6.6° and
 * 0.03 lightness apart, so under bloom the player's own bolts and the orbs circling the
 * ship arrive as one amber mass. The hues cannot move: §11.2 is ratified and the tight
 * pair is locked by test. What is left is §6.7 rule 7's other two axes — the two classes
 * already carry distinct silhouettes, and the separation is completed here by luminance:
 * bolts run hot toward gold-white, orbs run cool and desaturated.
 */
const EMISSIVE_BY_CLASS: readonly number[] = [
  2.4, 1.8, 2.0, 1.15, 1.8, 1.8, 1.8, 1.8, 1.8, 1.8, 1.8, 2.0, 1.2, 1.2, 1.2,
];

/**
 * AMD-7 R4: fraction to pull a class's hue toward its own luminance. Only the orbital
 * uses it — 20%, which is the other half of the lance/halo separation.
 */
const DESATURATE_BY_CLASS: readonly number[] = [
  0, 0, 0, 0.2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
];

/** True where the class is a beam and its instance scale means (length, half-width). */
const IS_BEAM: readonly boolean[] = [
  false, false, true, false, false, false, false, false, false, false, false, true, false, false, false,
];

/**
 * Visual class for a weapon or enemy attack id. Unknown ids fall back to the lance
 * class so a content typo draws something readable instead of nothing.
 */
export function visualClassForWeapon(weaponId: string): number {
  const vc = BY_WEAPON[weaponId];
  return vc === undefined ? VC_SPEAR : vc;
}

export function visualClassToken(vc: number): string {
  return TOKEN_BY_CLASS[vc] ?? TOKEN_BY_CLASS[VC_SPEAR];
}

export function visualClassEmissive(vc: number): number {
  return EMISSIVE_BY_CLASS[vc] ?? 1.8;
}

/** AMD-7 R4: 0 = pinned hue, 1 = fully grey at the same luminance. */
export function visualClassDesaturate(vc: number): number {
  return DESATURATE_BY_CLASS[vc] ?? 0;
}

export function visualClassIsBeam(vc: number): boolean {
  return IS_BEAM[vc] === true;
}

/** Classes drawn additively (§11.4: friendly projectiles and beams). Enemy shots blend normally. */
export function visualClassAdditive(vc: number): boolean {
  return vc < PLAYER_VISUAL_CLASS_COUNT;
}
